import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Clock, Play, Pause, RefreshCw, Bell, CheckCircle, XCircle, Wifi } from 'lucide-react';
import { useWebSocket } from '../context/WebSocketContext';
import api from '../lib/api';

export default function SchedulerPage() {
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [lastDriftCheck, setLastDriftCheck] = useState(null);
  const { isConnected } = useWebSocket();

  const fetchSchedulerStatus = async () => {
    try {
      const response = await api.get('/scheduler/status');
      setSchedulerStatus(response.data);
    } catch (error) {
      toast.error('Failed to fetch scheduler status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedulerStatus();
  }, []);

  const handleStartScheduler = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/scheduler/start');
      setSchedulerStatus(response.data.scheduler);
      toast.success(response.data.status === 'started' ? 'Scheduler started' : 'Scheduler already running');
    } catch (error) {
      toast.error('Failed to start scheduler');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStopScheduler = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/scheduler/stop');
      setSchedulerStatus(response.data.scheduler);
      toast.success(response.data.status === 'stopped' ? 'Scheduler stopped' : 'Scheduler was not running');
    } catch (error) {
      toast.error('Failed to stop scheduler');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunNow = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/scheduler/run-now');
      setLastDriftCheck(response.data);
      toast.success(`Drift check complete: ${response.data.frameworks_checked} frameworks checked`);
      if (response.data.frameworks_below_threshold > 0) {
        toast.warning(`${response.data.frameworks_below_threshold} frameworks below threshold`);
      }
    } catch (error) {
      toast.error('Failed to run drift check');
    } finally {
      setActionLoading(false);
    }
  };

  const formatInterval = (seconds) => {
    if (seconds >= 3600) {
      return `${Math.floor(seconds / 3600)} hour(s)`;
    }
    return `${Math.floor(seconds / 60)} minute(s)`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Compliance Drift Scheduler</h1>
            <p className="text-slate-500 mt-1">Automated compliance monitoring and alerting</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={isConnected ? "default" : "secondary"} className="flex items-center gap-1">
              <Wifi className="w-3 h-3" />
              {isConnected ? 'Real-time Connected' : 'Connecting...'}
            </Badge>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Scheduler Status */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Scheduler Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-full ${schedulerStatus?.running ? 'bg-green-100' : 'bg-slate-100'}`}>
                  {schedulerStatus?.running ? (
                    <Play className="w-6 h-6 text-green-600" />
                  ) : (
                    <Pause className="w-6 h-6 text-slate-400" />
                  )}
                </div>
                <div>
                  <p className="text-2xl font-bold">{schedulerStatus?.running ? 'Running' : 'Stopped'}</p>
                  <p className="text-sm text-slate-500">
                    Interval: {formatInterval(schedulerStatus?.interval_seconds || 3600)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Drift Threshold */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Drift Threshold</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-amber-100">
                  <Bell className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{schedulerStatus?.drift_threshold}%</p>
                  <p className="text-sm text-slate-500">
                    Alert when compliance drops below
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notification Channels */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Notification Channels</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Teams</span>
                  {schedulerStatus?.teams_configured ? (
                    <Badge variant="default" className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" /> Configured</Badge>
                  ) : (
                    <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" /> Not configured</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Slack</span>
                  {schedulerStatus?.slack_configured ? (
                    <Badge variant="default" className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" /> Configured</Badge>
                  ) : (
                    <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" /> Not configured</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Controls */}
        <Card>
          <CardHeader>
            <CardTitle>Scheduler Controls</CardTitle>
            <CardDescription>Manage automated compliance drift detection</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {schedulerStatus?.running ? (
                <Button 
                  onClick={handleStopScheduler} 
                  disabled={actionLoading}
                  variant="destructive"
                  data-testid="stop-scheduler-btn"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Stop Scheduler
                </Button>
              ) : (
                <Button 
                  onClick={handleStartScheduler} 
                  disabled={actionLoading}
                  data-testid="start-scheduler-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Scheduler
                </Button>
              )}
              
              <Button 
                onClick={handleRunNow} 
                disabled={actionLoading}
                variant="outline"
                data-testid="run-now-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                Run Check Now
              </Button>
              
              <Button 
                onClick={fetchSchedulerStatus} 
                variant="ghost"
                data-testid="refresh-status-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Status
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Last Drift Check Results */}
        {lastDriftCheck && (
          <Card>
            <CardHeader>
              <CardTitle>Last Drift Check Results</CardTitle>
              <CardDescription>
                Checked at: {new Date(lastDriftCheck.checked_at).toLocaleString()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Frameworks Checked</p>
                  <p className="text-2xl font-bold">{lastDriftCheck.frameworks_checked}</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-700">Below Threshold</p>
                  <p className="text-2xl font-bold text-amber-600">{lastDriftCheck.frameworks_below_threshold}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Threshold</p>
                  <p className="text-2xl font-bold">{lastDriftCheck.threshold}%</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">Alerts Sent</p>
                  <p className="text-2xl font-bold text-blue-600">{lastDriftCheck.alerts_sent?.length || 0}</p>
                </div>
              </div>

              {lastDriftCheck.alerts_sent && lastDriftCheck.alerts_sent.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2">Alerts Sent:</h4>
                  <div className="space-y-2">
                    {lastDriftCheck.alerts_sent.map((alert, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <span className="font-medium">{alert.framework}</span>
                        <span className="text-amber-700">{alert.compliance?.toFixed(1)}% compliance</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">How it works</h4>
                <p className="text-sm text-blue-700 mt-1">
                  The scheduler automatically checks compliance levels across all frameworks every hour. 
                  When any framework&apos;s compliance drops below the threshold ({schedulerStatus?.drift_threshold}%), 
                  alerts are sent to configured notification channels (Teams/Slack) and displayed in real-time 
                  to connected users via WebSocket.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
